import { BODY_HEIGHT, BODY_RADIUS, LEG_RADIUS, LEGS_TOP, STEP_UP } from "./body";
import type { Bounds, Vec3 } from "./colliderRules";

/** After a spawn or a server correction, entities this close get re-checked. */
export const RECHECK_RADIUS = 3;

/**
 * Could a collider with these bounds catch the player standing at feet? The body's band runs from STEP_UP to
 * BODY_HEIGHT above the feet, so feet on or just under the top face are standing on it, not inside; sideways it
 * reaches a leg radius for what stops below the hips and a body radius for what reaches above them (spec 2m-2 §4.3).
 */
export function overlapsBody(feet: Vec3, b: Bounds): boolean {
  const r = b.max.y > feet.y + LEGS_TOP ? BODY_RADIUS : LEG_RADIUS;
  return feet.x >= b.min.x - r && feet.x <= b.max.x + r
    && feet.z >= b.min.z - r && feet.z <= b.max.z + r
    && feet.y + BODY_HEIGHT > b.min.y && feet.y + STEP_UP < b.max.y;
}

function within(feet: Vec3, b: Bounds, r: number): boolean {
  return feet.x >= b.min.x - r && feet.x <= b.max.x + r && feet.z >= b.min.z - r && feet.z <= b.max.z + r
    && feet.y >= b.min.y - r && feet.y <= b.max.y + r;
}

/** Whether the player standing at feet is caught in the entity; the bounds test unless the entity has a finer one. */
export type Overlap = (feet: Vec3) => boolean;

interface Entry { bounds: Bounds; overlaps: Overlap; setCollides: (on: boolean) => void; suspended: boolean }

/**
 * A collider that appears around the player (placed at their feet, or loaded while they stand in it) would
 * shove them out through its nearest side, or hold them inside a solid; it stays off for them until they walk
 * clear (spec 2k §3.2, 2m §3.5).
 */
export class TrapGuard {
  private entries = new Map<string, Entry>();

  add(id: string, bounds: Bounds, setCollides: (on: boolean) => void, feet: Vec3 | null, overlaps: Overlap = (f) => overlapsBody(f, bounds)): void {
    const entry: Entry = { bounds, overlaps, setCollides, suspended: false };
    this.entries.set(id, entry);
    if (feet && overlaps(feet)) this.suspend(entry);
  }

  remove(id: string): void { this.entries.delete(id); }

  /** Suspend any nearby entity the player is now inside (after a spawn or correction). */
  recheck(feet: Vec3): void {
    for (const e of this.entries.values()) {
      if (!e.suspended && within(feet, e.bounds, RECHECK_RADIUS) && e.overlaps(feet)) this.suspend(e);
    }
  }

  /** Every frame: resume suspended entities the player has left. Never suspends. */
  tick(feet: Vec3): void {
    for (const e of this.entries.values()) {
      if (e.suspended && !e.overlaps(feet)) { e.suspended = false; e.setCollides(true); }
    }
  }

  suspendedIds(): string[] { return [...this.entries].filter(([, e]) => e.suspended).map(([id]) => id); }

  private suspend(e: Entry): void { e.suspended = true; e.setCollides(false); }
}
